import { useCallback } from 'react';
import { create } from 'zustand';
import { apiService, PanelFeatureDTO } from '@/services/api.service';
import type { PanelModuleKey } from '@/config/panelModules';
import { usePanelFeaturesStore } from '@/stores/panelFeatures.store';
import { useGameStore } from '@/stores/game.store';

/**
 * Activation, PAR PROJET, des modules du panel (Supervision, Configuration, Statistiques). Se
 * combine avec le drapeau global (panelFeatures.store) : effectif = global ET projet ET parent
 * (cf useModuleEnabled).
 *
 * Délibérément NON persisté, contrairement au store d'authentification : un admin qui active la
 * Lootbox pour un projet doit voir l'effet au rechargement suivant, pas au bout de la durée de vie
 * d'un cache local. Le coût est une requête au montage de l'application.
 *
 * Le serveur renvoie des valeurs déjà RÉSOLUES pour chaque projet connu (réglage, ancien réglage,
 * défaut du module). Tant que la carte n'est pas chargée, ou si elle échoue, chaque module prend le
 * défaut que lui donne le catalogue (projets par défaut), et à défaut de catalogue il est actif :
 * mieux vaut une nav trop large qu'une nav vide sur une erreur réseau.
 */
export type ProjectPageKey = PanelModuleKey;

type PagesMap = Record<string, Partial<Record<string, boolean>>>;

interface ProjectPagesState {
  /** { projectId: { moduleKey: actif } } */
  pages: PagesMap;
  /** Carte lue avec succès : seule condition pour ÉCRIRE une carte (sinon elle partirait des défauts). */
  loaded: boolean;
  /** Première tentative terminée, réussie ou non : le garde de route n'attend pas plus. */
  ready: boolean;
  fetch: () => Promise<void>;
  /** Actif pour ce projet (sans le drapeau global ni le parent) ? */
  isEnabled: (project: string, key: ProjectPageKey) => boolean;
  /** Applique localement un réglage qui vient d'être enregistré, sans refaire un aller-retour. */
  setLocal: (project: string, next: Partial<Record<string, boolean>>) => void;
}

/** Le module est-il actif d'office sur ce projet, sans réglage ? (catalogue serveur, sinon oui) */
function enabledByDefault(feature: PanelFeatureDTO | undefined, project: string): boolean {
  const projects = feature?.defaultProjects;
  return !projects || projects.includes(project);
}

/** Niveau projet seul : réglage résolu par le serveur, sinon défaut du catalogue, sinon actif. */
export function projectModuleEnabled(
  pages: PagesMap,
  features: PanelFeatureDTO[],
  project: string,
  key: string,
): boolean {
  const explicit = pages[project]?.[key];
  if (typeof explicit === 'boolean') return explicit;
  return enabledByDefault(features.find((f) => f.key === key), project);
}

/** Pourquoi un module est coupé : le module de la chaîne (lui, puis ses parents) et le niveau. */
export interface ModuleOff {
  key: string;
  scope: 'global' | 'project';
}

/**
 * Premier niveau qui coupe le module (drapeau global, puis réglage projet, puis parent), ou null
 * s'il est actif. Pure : sert à la navigation, au bandeau d'onglets et au garde de route.
 */
export function moduleOffReason(
  pages: PagesMap,
  features: PanelFeatureDTO[],
  project: string,
  key: string,
): ModuleOff | null {
  const seen = new Set<string>();
  let current: string | null | undefined = key;
  while (current && !seen.has(current)) {
    seen.add(current);
    const f = features.find((x) => x.key === current);
    if (f && !f.enabled) return { key: current, scope: 'global' };
    if (!projectModuleEnabled(pages, features, project, current)) return { key: current, scope: 'project' };
    current = f?.parent;
  }
  return null;
}

export const useProjectPagesStore = create<ProjectPagesState>((set, get) => ({
  pages: {},
  loaded: false,
  ready: false,

  fetch: async () => {
    const res = await apiService.getProjectPages();
    // Un échec laisse la carte en l'état (défauts du catalogue). `loaded` ne passe à vrai QUE sur
    // succès : l'onglet Fonctionnalités s'en sert pour refuser d'ÉCRIRE une carte construite sur
    // les défauts.
    if (res.data) set({ pages: res.data, loaded: true, ready: true });
    else set({ ready: true });
  },

  isEnabled: (project, key) =>
    projectModuleEnabled(get().pages, usePanelFeaturesStore.getState().features, project, key),

  setLocal: (project, next) => {
    set((s) => ({ pages: { ...s.pages, [project]: { ...s.pages[project], ...next } } }));
  },
}));

/**
 * Vérificateur d'activation EFFECTIVE d'un module (global ET projet ET parent), pour le projet
 * actif par défaut. S'abonne aux deux cartes : le composant se redessine quand elles arrivent.
 */
export function useModuleEnabled(): (key: ProjectPageKey, project?: string) => boolean {
  const pages = useProjectPagesStore((s) => s.pages);
  const features = usePanelFeaturesStore((s) => s.features);
  const activeGameId = useGameStore((s) => s.activeGameId);
  return useCallback(
    (key: ProjectPageKey, project?: string) => moduleOffReason(pages, features, project ?? activeGameId, key) === null,
    [pages, features, activeGameId],
  );
}
